import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollText, Sparkles, Loader2, BookOpen } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import { fetchLore, generateLore } from "../lib/api";

export const LoreFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    const data = await fetchLore().catch(() => []);
    setItems(data || []);
  };

  useEffect(() => { refresh(); }, []);

  const newLog = async () => {
    setLoading(true);
    try {
      const item = await generateLore();
      setItems([item, ...items]);
    } finally { setLoading(false); }
  };

  return (
    <div className="eigoo-glass rounded-3xl p-6 md:p-8" data-testid="lore-feed">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center">
            <ScrollText className="text-black" size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Commander's Log</div>
            <div className="font-display font-bold text-2xl md:text-3xl">Bridge Lore</div>
          </div>
          <SpeakerButton
            text="This is your Commander's Log. Tap a new entry to receive a fresh briefing from the CyberKids Fleet."
            testid="speaker-lore"
            size="sm"
          />
        </div>
        <button onClick={newLog} disabled={loading} className="btn-eigoo btn-gold" data-testid="generate-lore">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
          {loading ? "Beaming..." : "New Briefing"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-white/60">
          <BookOpen size={40} className="mx-auto mb-3 text-yellow-300/60" />
          <p>No lore yet. Tap "New Briefing" to receive your first transmission.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {items.slice(0, 5).map((item) => (
              <motion.article
                key={item.id}
                layout
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="rounded-2xl border border-yellow-300/15 bg-black/30 p-4 md:p-5"
                data-testid={`lore-item-${item.id}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <h4 className="font-display font-bold text-lg md:text-xl flex-1">{item.title}</h4>
                  <SpeakerButton text={`${item.title}. ${item.body}. Power word: ${item.vocab_word}. It means: ${item.vocab_meaning}.`} testid={`speaker-lore-${item.id}`} size="sm" />
                </div>
                <p className="text-white/85 text-base md:text-lg leading-relaxed">{item.body}</p>
                {item.vocab_word && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-yellow-300/10 border border-yellow-300/40 text-yellow-200 font-display font-bold text-sm">
                      Power Word: {item.vocab_word}
                    </span>
                    <span className="text-white/60 text-sm italic">{item.vocab_meaning}</span>
                  </div>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default LoreFeed;
